import { create } from "zustand";
import { createJSONStorage, persist, type StateStorage } from "zustand/middleware";

import type { BoxDraftState } from "./dropTableDraft";

/**
 * The box editor's unsaved drafts, one per box, with the history undo walks.
 *
 * It lives in the browser alone: nothing is saved on the server until the operator saves or
 * publishes. It is persisted to localStorage (key "loot-dashboard-box-drafts"), so a reload,
 * a detour to another page or a closed tab keeps the draft and its history; another browser
 * or another staff member does not see it.
 *
 * Box ids are ULIDs, unique across tenant boxes and box templates, so the id alone is the key.
 * An entry exists only once the operator has edited the box; `discard` removes it, which is the
 * editor's "revert to saved".
 */

/** The history undo walks in memory. */
export const MAX_HISTORY = 100;
/** The history a reload keeps. Snapshots are whole tables, so this bounds localStorage. */
export const MAX_PERSISTED_HISTORY = 25;
/** The boxes whose drafts a reload keeps; the least recently edited is dropped first. */
export const MAX_PERSISTED_DRAFTS = 10;
/** Keystrokes in one field closer together than this are one undo step. */
export const COALESCE_MS = 500;
/** How long a burst of edits waits before it is written to localStorage. */
const PERSIST_DEBOUNCE_MS = 500;

export interface BoxDraftEntry {
  /** The `currentVersion` the draft was built on; a publish pins it as `expectedVersion`. */
  baseVersion: number;
  present: BoxDraftState;
  past: BoxDraftState[];
  future: BoxDraftState[];
  touchedAt: number;
  /** The field the last edit typed into, so the next keystroke there joins the same step. */
  coalesce?: { key: string; at: number };
}

interface BoxDraftStoreState {
  drafts: Record<string, BoxDraftEntry>;
  /**
   * One edit of box `boxId`. `start` is the draft the box would show with no entry: the first
   * edit records it as the state to undo back to. `coalesce` names the field being typed in.
   */
  edit: (
    boxId: string,
    start: { version: number; state: BoxDraftState },
    recipe: (state: BoxDraftState) => BoxDraftState,
    coalesce?: string,
  ) => void;
  undo: (boxId: string) => void;
  redo: (boxId: string) => void;
  /** Throws the draft away: the editor shows the stored box again. */
  discard: (boxId: string) => void;
  /** Keeps the draft but builds it on `version` from now on, overwriting what that version changed. */
  rebase: (boxId: string, version: number) => void;
  /** Sign-out: another user on this browser shouldn't inherit a draft. */
  clearAll: () => void;
}

/** The entry after one edit: the old present joins the past unless the edit continues a burst. */
export function applyEdit(
  entry: BoxDraftEntry,
  recipe: (state: BoxDraftState) => BoxDraftState,
  coalesce: string | undefined,
  now: number,
): BoxDraftEntry {
  const next = recipe(entry.present);
  if (next === entry.present) return entry;
  const continues =
    coalesce !== undefined &&
    entry.coalesce?.key === coalesce &&
    now - entry.coalesce.at < COALESCE_MS &&
    entry.past.length > 0;
  return {
    ...entry,
    present: next,
    past: continues ? entry.past : [...entry.past, entry.present].slice(-MAX_HISTORY),
    future: [],
    touchedAt: now,
    coalesce: coalesce === undefined ? undefined : { key: coalesce, at: now },
  };
}

export function applyUndo(entry: BoxDraftEntry, now: number): BoxDraftEntry {
  const previous = entry.past.at(-1);
  if (!previous) return entry;
  return {
    ...entry,
    present: previous,
    past: entry.past.slice(0, -1),
    future: [entry.present, ...entry.future].slice(0, MAX_HISTORY),
    touchedAt: now,
    coalesce: undefined,
  };
}

export function applyRedo(entry: BoxDraftEntry, now: number): BoxDraftEntry {
  const [next, ...rest] = entry.future;
  if (!next) return entry;
  return {
    ...entry,
    present: next,
    past: [...entry.past, entry.present].slice(-MAX_HISTORY),
    future: rest,
    touchedAt: now,
    coalesce: undefined,
  };
}

/**
 * What a reload keeps: the most recently edited drafts, each with its latest history. `history`
 * is lowered to 0 when localStorage refuses the write, so a full quota costs the undo steps
 * before it costs a draft.
 */
export function persistedDrafts(
  drafts: Record<string, BoxDraftEntry>,
  history = MAX_PERSISTED_HISTORY,
): Record<string, BoxDraftEntry> {
  const kept = Object.entries(drafts)
    .sort(([, a], [, b]) => b.touchedAt - a.touchedAt)
    .slice(0, MAX_PERSISTED_DRAFTS)
    .map(([boxId, entry]): [string, BoxDraftEntry] => [
      boxId,
      {
        baseVersion: entry.baseVersion,
        present: entry.present,
        past: history > 0 ? entry.past.slice(-history) : [],
        future: history > 0 ? entry.future.slice(0, history) : [],
        touchedAt: entry.touchedAt,
      },
    ]);
  return Object.fromEntries(kept);
}

/**
 * localStorage behind a debounce, with every access in try/catch: a private window, cleared
 * site data or a full quota must never break the editor, only its memory across reloads. A
 * write that hits the quota is retried once without the history.
 */
function debouncedLocalStorage(): StateStorage {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let pending: { name: string; value: string } | undefined;

  const write = () => {
    timer = undefined;
    if (!pending) return;
    const { name, value } = pending;
    pending = undefined;
    try {
      localStorage.setItem(name, value);
    } catch {
      try {
        const parsed = JSON.parse(value) as { state: { drafts: Record<string, BoxDraftEntry> } };
        parsed.state.drafts = persistedDrafts(parsed.state.drafts, 0);
        localStorage.setItem(name, JSON.stringify(parsed));
      } catch {
        // Nothing more to shed: this reload will not remember the draft.
      }
    }
  };

  if (typeof window !== "undefined") {
    // A tab closed inside the debounce window still keeps its last keystroke.
    window.addEventListener("pagehide", write);
  }

  return {
    getItem: (name) => {
      try {
        return localStorage.getItem(name);
      } catch {
        return null;
      }
    },
    setItem: (name, value) => {
      pending = { name, value };
      if (timer !== undefined) clearTimeout(timer);
      timer = setTimeout(write, PERSIST_DEBOUNCE_MS);
    },
    removeItem: (name) => {
      pending = undefined;
      try {
        localStorage.removeItem(name);
      } catch {
        // As above: forgetting is best-effort too.
      }
    },
  };
}

function withEntry(
  drafts: Record<string, BoxDraftEntry>,
  boxId: string,
  update: (entry: BoxDraftEntry) => BoxDraftEntry,
): Record<string, BoxDraftEntry> {
  const entry = drafts[boxId];
  if (!entry) return drafts;
  const next = update(entry);
  return next === entry ? drafts : { ...drafts, [boxId]: next };
}

export const useBoxDraftStore = create<BoxDraftStoreState>()(
  persist(
    (set) => ({
      drafts: {},
      edit: (boxId, start, recipe, coalesce) =>
        set((s) => {
          const now = Date.now();
          const entry: BoxDraftEntry = s.drafts[boxId] ?? {
            baseVersion: start.version,
            present: start.state,
            past: [],
            future: [],
            touchedAt: now,
          };
          const next = applyEdit(entry, recipe, coalesce, now);
          return next === entry ? s : { drafts: { ...s.drafts, [boxId]: next } };
        }),
      undo: (boxId) =>
        set((s) => ({ drafts: withEntry(s.drafts, boxId, (e) => applyUndo(e, Date.now())) })),
      redo: (boxId) =>
        set((s) => ({ drafts: withEntry(s.drafts, boxId, (e) => applyRedo(e, Date.now())) })),
      discard: (boxId) =>
        set((s) => {
          if (!s.drafts[boxId]) return s;
          const { [boxId]: _gone, ...rest } = s.drafts;
          return { drafts: rest };
        }),
      rebase: (boxId, version) =>
        set((s) => ({
          drafts: withEntry(s.drafts, boxId, (e) => ({ ...e, baseVersion: version })),
        })),
      clearAll: () => set({ drafts: {} }),
    }),
    {
      name: "loot-dashboard-box-drafts",
      version: 1,
      storage: createJSONStorage(debouncedLocalStorage),
      partialize: (s) => ({ drafts: persistedDrafts(s.drafts) }),
    },
  ),
);
